import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import { cn } from '../../lib/utils';
import { UNASSIGNED_AM } from '../../lib/accountAnalytics';
import type {
  AccountAnalyticsFilterOptions,
  AccountAnalyticsFilters,
  AmDirectoryEntry,
} from '../../types/accountAnalytics';

const opt = (o: { value: string; label: string }): MultiSelectOption => ({
  value: o.value,
  label: o.label,
});

// Filter bar for both lenses (shared). Multi-select chips for Brand / AM / Group
// / Industry / Type / Status + the scope toggle (Locations & Dealerships vs All
// types). Options come from deriveFilterOptions over the roster; the AM options
// come from the full AM directory (so you can filter by an AM with 0 accounts)
// unioned with the Unassigned sentinel.
export function AnalyticsFilterBar({
  options,
  amDirectory,
  selection,
  onChange,
  hasUnassigned,
  testId,
}: {
  options: AccountAnalyticsFilterOptions;
  amDirectory: AmDirectoryEntry[];
  selection: AccountAnalyticsFilters;
  onChange: (next: AccountAnalyticsFilters) => void;
  // Whether any roster row is unassigned (drives showing the Unassigned option).
  hasUnassigned: boolean;
  // Distinguishes the two rendered instances (top + table) so tests can scope
  // queries with `within` — the bar renders twice against the same state.
  testId?: string;
}) {
  const patch = (p: Partial<AccountAnalyticsFilters>) => onChange({ ...selection, ...p });

  const amOptions: MultiSelectOption[] = [
    ...amDirectory.map((a) => ({ value: a.uid, label: a.displayName ?? a.uid })),
    ...(hasUnassigned ? [{ value: UNASSIGNED_AM, label: 'Unassigned' }] : []),
  ];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <MultiSelectFilter
        label="Brand"
        options={options.brands.map(opt)}
        selected={selection.brand}
        onChange={(v) => patch({ brand: v })}
      />
      <MultiSelectFilter
        label="AM"
        options={amOptions}
        selected={selection.accountManagerId}
        onChange={(v) => patch({ accountManagerId: v })}
      />
      <MultiSelectFilter
        label="Group"
        options={options.groups.map(opt)}
        selected={selection.group}
        onChange={(v) => patch({ group: v })}
      />
      <MultiSelectFilter
        label="Industry"
        options={options.industries.map(opt)}
        selected={selection.industry}
        onChange={(v) => patch({ industry: v })}
      />
      <MultiSelectFilter
        label="Type"
        options={options.accountTypes.map(opt)}
        selected={selection.accountType}
        onChange={(v) => patch({ accountType: v })}
      />
      <MultiSelectFilter
        label="Status"
        options={options.statuses.map(opt)}
        selected={selection.status}
        onChange={(v) => patch({ status: v })}
      />
      <ScopeToggle scope={selection.scope} onChange={(scope) => patch({ scope })} />
    </div>
  );
}

// Locations & Dealerships (operational) vs All account types.
function ScopeToggle({
  scope,
  onChange,
}: {
  scope: 'operational' | 'all';
  onChange: (scope: 'operational' | 'all') => void;
}) {
  const btn = (value: 'operational' | 'all', label: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={cn(
        'px-3 py-1 text-xs font-medium transition-colors',
        scope === value ? 'bg-kyck-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="ml-1 inline-flex overflow-hidden rounded-md border border-slate-300">
      {btn('operational', 'Locations & Dealerships')}
      {btn('all', 'All types')}
    </div>
  );
}
